import { useCallback, useEffect, useRef, useState } from 'react';
import axios, { CancelTokenSource } from 'axios';
import {
    SreEntityType,
    SreProgressResponse,
} from '../../../../../AGSS/models/SreHealth';
import { getInflightProgress, AgrmType } from '../../../../../AGSS/api/AGRMApi';
import {
    readSreCache,
    sreCacheScope,
    SRE_CACHE_MAX_AGE_MS,
    writeSreCache,
} from './sreSessionCache';

export interface SreProgressEntry {
    data?: SreProgressResponse;
    isLoading: boolean;
    error?: string;
    updatedAt?: number;
}

export interface SreProgressState {
    get: (uniqueIdentifier: string) => SreProgressEntry | undefined;
    load: (uniqueIdentifier: string, force?: boolean) => void;
    cancel: (uniqueIdentifier: string) => void;
}

export const useSreProgress = (type: SreEntityType): SreProgressState => {
    const scope = `${sreCacheScope()}:${type}:progress`;
    const entries = useRef<Record<string, SreProgressEntry>>({});
    const requests = useRef(new Map<string, CancelTokenSource>());
    const [, setRevision] = useState(0);
    const mounted = useRef(false);
    const keyFor = useCallback(
        (identifier: string) => `${scope}:${identifier}`,
        [scope]
    );

    const publish = useCallback((key: string, entry: SreProgressEntry) => {
        if (!mounted.current) return;
        entries.current[key] = entry;
        setRevision((revision) => revision + 1);
    }, []);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            requests.current.forEach((source) =>
                source.cancel('Progress view closed')
            );
            requests.current.clear();
            entries.current = {};
        };
    }, [scope]);

    const get = useCallback(
        (identifier: string): SreProgressEntry | undefined => {
            const key = keyFor(identifier);
            if (entries.current[key]) return entries.current[key];
            const cached = readSreCache<SreProgressResponse>(key);
            return cached
                ? {
                      data: cached.data,
                      updatedAt: cached.savedAt,
                      isLoading: false,
                  }
                : undefined;
        },
        [keyFor]
    );

    const cancel = useCallback(
        (identifier: string) => {
            const key = keyFor(identifier);
            const source = requests.current.get(key);
            requests.current.delete(key);
            source?.cancel('Progress no longer visible');
            const existing = entries.current[key];
            if (existing?.isLoading)
                publish(key, { ...existing, isLoading: false });
            else if (existing) {
                delete entries.current[key];
                if (mounted.current) setRevision((revision) => revision + 1);
            }
        },
        [keyFor, publish]
    );

    const load = useCallback(
        (identifier: string, force = false) => {
            const key = keyFor(identifier);
            const existing = get(identifier);
            if (
                !force &&
                (requests.current.has(key) ||
                    (existing?.data &&
                        Date.now() - (existing.updatedAt ?? 0) <
                            SRE_CACHE_MAX_AGE_MS))
            )
                return;
            requests.current.get(key)?.cancel('Superseded by refresh');
            const source = axios.CancelToken.source();
            requests.current.set(key, source);
            publish(key, { ...existing, isLoading: true, error: undefined });
            const ownsRequest = () =>
                mounted.current && requests.current.get(key) === source;

            getInflightProgress(
                identifier,
                type === 'build' ? AgrmType.Build : AgrmType.Deployment,
                source.token
            )
                .then((result) => {
                    if (!ownsRequest()) return;
                    if (!result?.succeeded || !result.data)
                        throw new Error(
                            result?.message ??
                                'No progress information returned.'
                        );
                    writeSreCache(key, result.data);
                    publish(key, {
                        data: result.data,
                        isLoading: false,
                        updatedAt: Date.now(),
                    });
                })
                .catch((error) => {
                    if (!ownsRequest()) return;
                    publish(key, {
                        ...existing,
                        isLoading: false,
                        error: axios.isCancel(error)
                            ? undefined
                            : error?.response?.data?.message ??
                              error?.message ??
                              'Failed to load progress.',
                    });
                })
                .finally(() => {
                    if (requests.current.get(key) === source)
                        requests.current.delete(key);
                });
        },
        [get, keyFor, publish, type]
    );

    return { get, load, cancel };
};
